import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    LinearProgress,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    CircularProgress,
    Autocomplete,
} from "@mui/material";
import { CheckCircle, Search } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";

const TeacherCourseUpdates = () => {
    const { selectedAcademicYear: selectedYear, setSelectedAcademicYear: setSelectedYear } =
        useSelectedAcademicYear();
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState("");
    const [teacherCourseData, setTeacherCourseData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [selectedProgress, setSelectedProgress] = useState(null);

    // Fetch schools on component mount
    useEffect(() => {
        const fetchSchools = async () => {
            try {
                const { data, error } = await supabase
                    .from("schools")
                    .select("id, name, district")
                    .eq("status", "Active")
                    .order("name");

                if (error) throw error;
                setSchools(data || []);
            } catch (error) {
                console.error("Error fetching schools:", error);
                setErrorMessage("Failed to load schools");
            }
        };

        fetchSchools();
    }, []);

    // Fetch teacher course data when school is selected
    useEffect(() => {
        if (!selectedSchool) {
            setTeacherCourseData([]);
            return;
        }

        const fetchTeacherCourseData = async () => {
            setLoading(true);
            try {
                // Fetch teachers from selected school
                const { data: teachers, error: teachersError } = await supabase
                    .from("profiles")
                    .select("id, full_name, email")
                    .eq("school_id", selectedSchool)
                    .eq("role", "ROLE_SCHOOL_TEACHER")
                    .eq("status", "Active");

                if (teachersError) throw teachersError;

                if (!teachers || teachers.length === 0) {
                    setTeacherCourseData([]);
                    setLoading(false);
                    return;
                }

                const teacherIds = teachers.map((t) => t.id);

                // Fetch teacher grades
                const { data: teacherGrades, error: gradesError } = await supabase
                    .from("teacher_grades")
                    .select("profile_id, grade_id")
                    .in("profile_id", teacherIds);

                if (gradesError) throw gradesError;

                // Get all unique grade IDs
                const allGradeIds = [...new Set(teacherGrades.map((tg) => tg.grade_id))];

                // Fetch grade details with parent_grade
                const { data: grades, error: gradeDetailsError } = await supabase
                    .from("grades")
                    .select("id, name, parent_grade")
                    .in("id", allGradeIds);

                if (gradeDetailsError) throw gradeDetailsError;

                // Create grade map
                const gradeMap = {};
                grades.forEach((grade) => {
                    gradeMap[grade.id] = grade;
                });

                // Build teacher -> grades map with parent_grade info
                const teacherGradeMap = {};
                teacherGrades.forEach((tg) => {
                    if (!teacherGradeMap[tg.profile_id]) {
                        teacherGradeMap[tg.profile_id] = [];
                    }
                    const gradeInfo = gradeMap[tg.grade_id];
                    if (gradeInfo) {
                        teacherGradeMap[tg.profile_id].push({
                            id: tg.grade_id,
                            name: gradeInfo.name,
                            parent_grade: gradeInfo.parent_grade || gradeInfo.name,
                        });
                    }
                });

                // Fetch ALL courses available for these grades
                const { data: categoryGrades, error: categoryGradesError } = await supabase
                    .from("category_grades")
                    .select(`
						category_id,
						grade_id,
						course_categories (
							id,
							name,
							description,
							status
						)
					`)
                    .in("grade_id", allGradeIds);

                if (categoryGradesError) throw categoryGradesError;

                // Build grade -> categories map
                const gradeCategoryMap = {};
                categoryGrades.forEach((cg) => {
                    if (cg.course_categories?.status) {
                        if (!gradeCategoryMap[cg.grade_id]) {
                            gradeCategoryMap[cg.grade_id] = [];
                        }
                        gradeCategoryMap[cg.grade_id].push({
                            id: cg.category_id,
                            name: cg.course_categories.name,
                            description: cg.course_categories.description,
                        });
                    }
                });

                // Fetch existing course progress
                const { data: courseProgress, error: progressError } = await supabase
                    .from("teacher_course_progress")
                    .select("id, teacher_id, category_id, grade_id, status, completion_percentage")
                    .in("teacher_id", teacherIds)
                    .eq("academic_year", selectedYear);

                if (progressError) throw progressError;

                // Build combined data: one row per teacher-course-parent_grade combination
                const combinedData = [];
                const seenCombinations = new Set();

                teachers.forEach((teacher) => {
                    const teacherGradesList = teacherGradeMap[teacher.id] || [];

                    // Group teacher's grades by parent_grade
                    const teacherParentGradeGroups = {};
                    teacherGradesList.forEach((grade) => {
                        const parentGrade = grade.parent_grade;
                        if (!teacherParentGradeGroups[parentGrade]) {
                            teacherParentGradeGroups[parentGrade] = [];
                        }
                        teacherParentGradeGroups[parentGrade].push(grade);
                    });

                    // For each parent grade group
                    Object.keys(teacherParentGradeGroups).forEach((parentGrade) => {
                        const gradesInGroup = teacherParentGradeGroups[parentGrade];
                        const gradeIdsInGroup = gradesInGroup.map((g) => g.id);

                        // Get all unique courses for this parent grade group
                        const coursesForParentGrade = new Set();
                        gradesInGroup.forEach((grade) => {
                            const coursesForGrade = gradeCategoryMap[grade.id] || [];
                            coursesForGrade.forEach((course) => {
                                coursesForParentGrade.add(JSON.stringify(course));
                            });
                        });

                        // For each unique course in this parent grade
                        coursesForParentGrade.forEach((courseStr) => {
                            const course = JSON.parse(courseStr);
                            const combinationKey = `${teacher.id}-${course.id}-${parentGrade}`;

                            if (!seenCombinations.has(combinationKey)) {
                                seenCombinations.add(combinationKey);

                                // Find progress records for this teacher-course-parent_grade combination
                                const progressRecords = (courseProgress || []).filter(
                                    (p) =>
                                        p.teacher_id === teacher.id &&
                                        p.category_id === course.id &&
                                        gradeIdsInGroup.includes(p.grade_id)
                                );

                                // Calculate aggregated status for this parent grade
                                let status = "not_started";
                                let completionPercentage = 0;
                                let progressIds = [];

                                if (progressRecords.length > 0) {
                                    const allCompleted = progressRecords.every(
                                        (p) => p.status === "completed"
                                    );
                                    const anyInProgress = progressRecords.some(
                                        (p) => p.status === "in_progress"
                                    );

                                    if (allCompleted) {
                                        status = "completed";
                                        completionPercentage = 100;
                                    } else if (anyInProgress || progressRecords.length > 0) {
                                        status = "in_progress";
                                        const totalCompletion = progressRecords.reduce(
                                            (sum, p) => sum + (p.completion_percentage || 0),
                                            0
                                        );
                                        completionPercentage = Math.round(
                                            totalCompletion / progressRecords.length
                                        );
                                    }

                                    progressIds = progressRecords.map((p) => p.id);
                                }

                                // Get grade names for this parent grade group
                                const gradeNames = gradesInGroup.map((g) => g.name).join(", ");

                                combinedData.push({
                                    progressIds: progressIds,
                                    teacherId: teacher.id,
                                    teacherName: teacher.full_name,
                                    teacherEmail: teacher.email,
                                    courseName: course.name,
                                    gradeName: gradeNames,
                                    parentGrade: parentGrade,
                                    status: status,
                                    completionPercentage: completionPercentage,
                                    categoryId: course.id,
                                    teacherGrades: gradesInGroup,
                                });
                            }
                        });
                    });
                });

                setTeacherCourseData(combinedData);
            } catch (error) {
                console.error("Error fetching teacher course data:", error);
                setErrorMessage("Failed to load teacher course data");
            } finally {
                setLoading(false);
            }
        };

        fetchTeacherCourseData();
    }, [selectedSchool, selectedYear]);

    // Filter data based on search query
    const filteredData = teacherCourseData.filter(
        (item) =>
            item.teacherName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.teacherEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.courseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.gradeName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Handle mark as completed
    const handleMarkAsCompleted = async () => {
        if (!selectedProgress) return;

        try {
            setLoading(true);

            const teacherGrades = selectedProgress.teacherGrades || [];
            const { data: allContents, error: contentsError } = await supabase
                .from("course_contents")
                .select("id")
                .eq("category_id", selectedProgress.categoryId)
                .eq("status", true);

            if (contentsError) throw contentsError;

            const primaryGradeId = teacherGrades[0]?.id;
            if (!primaryGradeId) {
                throw new Error("No grades found for this course");
            }

            // For each grade in this parent grade group
            for (const grade of teacherGrades) {
                const { data: existingCourseProgress } = await supabase
                    .from("teacher_course_progress")
                    .select("id")
                    .eq("teacher_id", selectedProgress.teacherId)
                    .eq("category_id", selectedProgress.categoryId)
                    .eq("grade_id", grade.id)
                    .eq("academic_year", selectedYear)
                    .maybeSingle();

                if (!existingCourseProgress) {
                    await supabase.from("teacher_course_progress").insert({
                        teacher_id: selectedProgress.teacherId,
                        category_id: selectedProgress.categoryId,
                        grade_id: grade.id,
                        status: "completed",
                        completion_percentage: 100,
                        academic_year: selectedYear,
                    });
                } else {
                    await supabase
                        .from("teacher_course_progress")
                        .update({
                            status: "completed",
                            completion_percentage: 100,
                        })
                        .eq("id", existingCourseProgress.id);
                }
            }

            // Content progress is per teacher+category+content+year (not per grade).
            // grade_id is required so Game Results Upload can filter by class.
            const { data: existingProgress } = await supabase
                .from("teacher_content_progress")
                .select("content_id")
                .eq("teacher_id", selectedProgress.teacherId)
                .eq("category_id", selectedProgress.categoryId)
                .eq("academic_year", selectedYear);

            const existingContentIds = new Set(
                (existingProgress || []).map((p) => p.content_id)
            );

            const completedAt = new Date().toISOString();
            const contentProgressPatch = {
                status: "completed",
                progress_data: { progress: 100 },
                completed_at: completedAt,
                grade_id: primaryGradeId,
            };

            if (existingContentIds.size > 0) {
                const { error: updateContentError } = await supabase
                    .from("teacher_content_progress")
                    .update(contentProgressPatch)
                    .eq("teacher_id", selectedProgress.teacherId)
                    .eq("category_id", selectedProgress.categoryId)
                    .eq("academic_year", selectedYear);

                if (updateContentError) throw updateContentError;
            }

            const contentsToCreate = (allContents || []).filter(
                (content) => !existingContentIds.has(content.id)
            );

            if (contentsToCreate.length > 0) {
                const newProgressRecords = contentsToCreate.map((content) => ({
                    teacher_id: selectedProgress.teacherId,
                    category_id: selectedProgress.categoryId,
                    content_id: content.id,
                    grade_id: primaryGradeId,
                    status: "completed",
                    progress_data: { progress: 100 },
                    completed_at: completedAt,
                    academic_year: selectedYear,
                }));

                const { error: insertContentError } = await supabase
                    .from("teacher_content_progress")
                    .insert(newProgressRecords);

                if (insertContentError) throw insertContentError;
            }

            // Update local state
            setTeacherCourseData((prev) =>
                prev.map((item) =>
                    item.teacherId === selectedProgress.teacherId &&
                        item.categoryId === selectedProgress.categoryId &&
                        item.parentGrade === selectedProgress.parentGrade
                        ? { ...item, status: "completed", completionPercentage: 100 }
                        : item
                )
            );

            setSuccessMessage(
                `Successfully marked "${selectedProgress.courseName}" as completed for ${selectedProgress.teacherName}`
            );
            setConfirmDialog(false);
            setSelectedProgress(null);
        } catch (error) {
            console.error("Error marking course as completed:", error);
            setErrorMessage("Failed to mark course as completed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const openConfirmDialog = (progress) => {
        setSelectedProgress(progress);
        setConfirmDialog(true);
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: "#111827" }}>
                Teacher Course Updates
            </Typography>

            {/* School Filter */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <AcademicYearSelector
                        value={selectedYear}
                        onChange={setSelectedYear}
                    />

                    <Autocomplete
                        options={schools}
                        getOptionLabel={(option) =>
                            option.district ? `${option.name} - ${option.district}` : option.name
                        }
                        value={schools.find((s) => s.id === selectedSchool) || null}
                        onChange={(event, newValue) => {
                            setSelectedSchool(newValue ? newValue.id : "");
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Select School" placeholder="Search schools..." />
                        )}
                        sx={{ minWidth: 300 }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />

                    {selectedSchool && (
                        <TextField
                            placeholder="Search teachers or courses..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="small"
                            sx={{ minWidth: 300 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search size={18} color="#6b7280" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                </Box>
            </Paper>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* No School Selected */}
            {!selectedSchool && !loading && (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                        Please select a school to view teacher course data
                    </Typography>
                </Paper>
            )}

            {/* No Data */}
            {selectedSchool && !loading && filteredData.length === 0 && (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                        {searchQuery
                            ? "No results found matching your search"
                            : "No course data found for teachers in this school"}
                    </Typography>
                </Paper>
            )}

            {/* Data Table */}
            {selectedSchool && !loading && filteredData.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                                <TableCell sx={{ fontWeight: 600 }}>Teacher Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Course Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((item, index) => (
                                <TableRow key={`${item.teacherId}-${item.categoryId}-${item.parentGrade}-${index}`} hover>
                                    <TableCell>{item.teacherName}</TableCell>
                                    <TableCell sx={{ fontSize: "0.875rem", color: "#6b7280" }}>
                                        {item.teacherEmail}
                                    </TableCell>
                                    <TableCell>{item.courseName}</TableCell>
                                    <TableCell>{item.gradeName}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={
                                                item.status === "completed"
                                                    ? "Completed"
                                                    : item.status === "in_progress"
                                                        ? "In Progress"
                                                        : "Not Started"
                                            }
                                            size="small"
                                            sx={{
                                                backgroundColor:
                                                    item.status === "completed"
                                                        ? "#ecfdf5"
                                                        : item.status === "in_progress"
                                                            ? "#fef3c7"
                                                            : "#f3f4f6",
                                                color:
                                                    item.status === "completed"
                                                        ? "#059669"
                                                        : item.status === "in_progress"
                                                            ? "#d97706"
                                                            : "#6b7280",
                                                fontWeight: 500,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={item.completionPercentage}
                                                sx={{
                                                    width: 100,
                                                    height: 6,
                                                    borderRadius: 1,
                                                    backgroundColor: "#e5e7eb",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor:
                                                            item.status === "completed"
                                                                ? "#059669"
                                                                : "#6366f1",
                                                    },
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{ color: "#6b7280", minWidth: 40 }}>
                                                {item.completionPercentage}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {item.status === "completed" ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "#059669", fontWeight: 500 }}>
                                                ✓ Completed
                                            </Typography>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<CheckCircle size={16} />}
                                                onClick={() => openConfirmDialog(item)}
                                                sx={{
                                                    backgroundColor: "#059669",
                                                    "&:hover": {
                                                        backgroundColor: "#047857",
                                                    },
                                                    textTransform: "none",
                                                }}>
                                                Mark Complete
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog}
                onClose={() => setConfirmDialog(false)}
                maxWidth="sm"
                fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Mark Course as Completed?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to mark the course "
                        <strong>{selectedProgress?.courseName}</strong>" as completed for{" "}
                        <strong>{selectedProgress?.teacherName}</strong>?
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2 }}>
                        This will:
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                        <Typography variant="body2" color="text.secondary" component="li">
                            Update the course status to "Completed"
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="li">
                            Set completion percentage to 100%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="li">
                            Mark all content items as completed
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setConfirmDialog(false)}
                        variant="outlined"
                        disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMarkAsCompleted}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            backgroundColor: "#059669",
                            "&:hover": {
                                backgroundColor: "#047857",
                            },
                        }}>
                        {loading ? "Processing..." : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={4000}
                onClose={() => setSuccessMessage("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert
                    onClose={() => setSuccessMessage("")}
                    severity="success"
                    sx={{ width: "100%" }}>
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Error Snackbar */}
            <Snackbar
                open={!!errorMessage}
                autoHideDuration={4000}
                onClose={() => setErrorMessage("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert
                    onClose={() => setErrorMessage("")}
                    severity="error"
                    sx={{ width: "100%" }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TeacherCourseUpdates;
